/**
 * @file `useDebounce` — small generic debouncer for high-frequency
 *       inputs. Used by `useLearners` to keep the search-term
 *       update from firing a fetch per keystroke.
 * @module @labcraft/admin/ui/hooks/use-debounce
 *
 * Returns a single value that lags the input by `delayMs`. Pair with
 * a queryKey factory (NOT a queryFn body) so TanStack's
 * structural-equality dedup catches: while typing, only the final
 * key change drives a fetch.
 */

import { useEffect, useState } from 'react'

/**
 * Defer a value for `delayMs` after the last change. Reset on every
 * input change; the timeout that resolves last wins.
 *
 * @typeParam T - Any serializable value.
 * @param value - The live input value.
 * @param delayMs - Debounce window in milliseconds.
 */
export function useDebounce<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebounced(value)
    }, delayMs)
    return () => {
      window.clearTimeout(id)
    }
  }, [value, delayMs])
  return debounced
}
